import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";

import type { TErrorResponse } from "@/app/_module/services/generalTypes";
import { successToast } from "@/app/_module/services/helper";

import { leadsQueryKeys } from "../services/keys";
import { leadsService } from "../services/services";
import type {
  TActivateLeadsImportParams,
  TActivateLeadsImportResponseData,
  TActivateSingleImportResponseData,
  TFileUploadResponse,
  TGetLeadsImportbyIdParams,
} from "../types/lead-import-types";

export const useUploadLeads = () => {
  const queryClient = useQueryClient();
  
  return useMutation<TFileUploadResponse, TErrorResponse, FormData>({
    mutationKey: [leadsQueryKeys.create, "upload-leads"],
    mutationFn: (uploadLeadParams: FormData) =>
      leadsService.uploadLead(uploadLeadParams),
    onSuccess: async () => {
      successToast("Lead upload successful");
      
      // Invalidate leads queries to refresh the data
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.overview() }),
          queryClient.invalidateQueries({ queryKey: [leadsQueryKeys.read, "getLeadsImportList"] })
        ]);
      } catch (error) {

      }
    },
    // Enhanced retry logic for file uploads
    retry: (failureCount, error) => {
      if (failureCount < 2) { // 2 retries for uploads
        const errorMessage = error && typeof error === 'object' && 'message' in error 
          ? (error as { message: string }).message 
          : '';
          
        if (errorMessage.includes('500') || 
            errorMessage.includes('502') || 
            errorMessage.includes('503') ||
            errorMessage.includes('timeout') || 
            errorMessage.includes('Network')) {
          return true;
        }
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1500 * (attemptIndex + 1), 5000), // Longer delays for uploads
  });
};

export const useGetLeadsImportList = (options?: { refetchInterval?: number | false }) => {
  return useQuery({
    queryKey: [leadsQueryKeys.read, "getLeadsImportList"],
    queryFn: () => leadsService.getLeadsImportList(),
    staleTime: 0, // Always consider data stale for real-time updates
    refetchOnMount: 'always', // Always refetch on mount
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(400 * (attemptIndex + 1), 2000),
    refetchOnWindowFocus: true,
    refetchInterval: options?.refetchInterval ?? false, // Poll when specified (e.g., 5000ms for 5 seconds)
  });
};

// Soft-deleted lists (Recently Deleted → Lists tab). Same shape as the active
// import list, so the row renderer can be reused.
export const useGetDeletedImports = (options?: { enabled?: boolean }) => {
  return useQuery({
    queryKey: [leadsQueryKeys.read, "getDeletedImports"],
    queryFn: () => leadsService.getDeletedImports(),
    enabled: options?.enabled !== false,
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  });
};

// Restore a whole deleted list (undo the delete). Un-deletes the import + the
// leads/households it removed. Refreshes both the deleted-list view and the
// active lists/leads.
export const useRestoreImport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [leadsQueryKeys.read, "restoreImport"],
    mutationFn: (importId: string) => leadsService.restoreImport(importId),
    onSuccess: async (data) => {
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [leadsQueryKeys.read, "getDeletedImports"] }),
          queryClient.invalidateQueries({ queryKey: [leadsQueryKeys.read, "getLeadsImportList"] }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.overview() }),
          queryClient.invalidateQueries({ queryKey: ["leads"] }),
          queryClient.invalidateQueries({ queryKey: ["unassignedLeads"] }),
        ]);
      } catch {
        // refreshes on next navigation
      }
      const r = data as { restoredLeads?: number; alreadyActive?: boolean } | undefined;
      if (r?.alreadyActive) {
        successToast("This list is already active.");
      } else {
        const n = r?.restoredLeads ?? 0;
        successToast(`Restored ${n.toLocaleString()} lead${n === 1 ? "" : "s"} from this list.`);
      }
    },
  });
};

// Start a background bulk delete of multiple lists. Returns { jobId, totalLists }.
// On success we refetch the jobs query so the global progress widget picks up
// the brand-new job immediately and starts polling — otherwise the widget
// (which only polls while a job is active) never notices the job started and
// never shows progress or the "done" notification.
export const useBulkDeleteImports = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: [leadsQueryKeys.read, "bulkDeleteImports"],
    mutationFn: (importIds: string[]) => leadsService.bulkDeleteImports(importIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [leadsQueryKeys.read, "bulkDeleteJobs"] });
    },
  });
};

// Poll the user's recent bulk-delete jobs. Polls every 2.5s while any job is
// still running so the global widget can show live progress + a done toast.
export const useBulkDeleteJobs = (options?: { enabled?: boolean }) => {
  return useQuery<any[]>({
    queryKey: [leadsQueryKeys.read, "bulkDeleteJobs"],
    queryFn: () => leadsService.getBulkDeleteJobs(),
    enabled: options?.enabled !== false,
    refetchInterval: (query) => {
      const jobs = (query.state.data as any[]) || [];
      const active = jobs.some((j) => j?.status === "queued" || j?.status === "processing");
      return active ? 2500 : false;
    },
    staleTime: 0,
  });
};

export const useGetLeadsImportById = (
  importId: string,
  param?: TGetLeadsImportbyIdParams
) => {
  return useQuery({
    queryKey: [leadsQueryKeys.read, "getLeadsImportById", importId],
    queryFn: () => leadsService.getLeadsImportById(importId, param),
    enabled: !!importId,
    staleTime: 30 * 1000, // 30 seconds - import details change frequently
    refetchOnMount: true, // Ensure fresh data on mount
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(300 * (attemptIndex + 1), 1500),
    refetchOnWindowFocus: true,
  });
};

export const useActivateLeadsImportById = () => {
  const queryClient = useQueryClient();
  
  return useMutation<
    TActivateLeadsImportResponseData,
    TErrorResponse,
    { importId: string; param: TActivateLeadsImportParams }
  >({
    mutationKey: [leadsQueryKeys.read, "activateLeadsImportById"],
    mutationFn: ({ importId, param }) =>
      leadsService.activateLeadsImport(importId, param),
    onMutate: async () => {
      // Cancel any outgoing refetches
      await Promise.all([
        queryClient.cancelQueries({ queryKey: leadsQueryKeys.lists() }),
        queryClient.cancelQueries({ queryKey: leadsQueryKeys.stats() }),
        queryClient.cancelQueries({ queryKey: leadsQueryKeys.overview() })
      ]);
      
      // Snapshot previous values
      const previousLeads = queryClient.getQueryData(leadsQueryKeys.lists());
      const previousStats = queryClient.getQueryData(leadsQueryKeys.stats());
      const previousOverview = queryClient.getQueryData(leadsQueryKeys.overview());
      
      return { previousLeads, previousStats, previousOverview };
    },
    onSuccess: async (_data, variables) => {
      // Invalidate ALL leads-related queries to ensure the table refreshes
      // Use broad key patterns to catch all variations (with/without agency context)
      try {
        await Promise.all([
          // Invalidate by specific keys
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.overview() }),
          queryClient.invalidateQueries({ queryKey: [leadsQueryKeys.read, "getLeadsImportList"] }),
          queryClient.invalidateQueries({ queryKey: [leadsQueryKeys.read, "getLeadsImportById", variables.importId] }),
          // Also invalidate ALL queries that start with "leads" to catch any variations
          queryClient.invalidateQueries({ queryKey: ["leads"] }),
        ]);
      } catch (error) {
        // Silently fail - the data will refresh on next navigation
      }

      successToast("Leads imported and activated successfully");
    },
    onError: (error, variables, context) => {

      // Restore previous values on error
      const ctx = context as { previousLeads?: any; previousStats?: any; previousOverview?: any } | undefined;
      if (ctx?.previousLeads) {
        queryClient.setQueryData(leadsQueryKeys.lists(), ctx.previousLeads);
      }
      if (ctx?.previousStats) {
        queryClient.setQueryData(leadsQueryKeys.stats(), ctx.previousStats);
      }
      if (ctx?.previousOverview) {
        queryClient.setQueryData(leadsQueryKeys.overview(), ctx.previousOverview);
      }
    },
    // Enhanced retry logic for import operations
    retry: (failureCount, error) => {
      if (failureCount < 3) {
        const errorMessage = error && typeof error === 'object' && 'message' in error 
          ? (error as { message: string }).message 
          : '';
          
        if (errorMessage.includes('500') || 
            errorMessage.includes('502') || 
            errorMessage.includes('503') ||
            errorMessage.includes('timeout') || 
            errorMessage.includes('Network')) {
          return true;
        }
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(1000 * (attemptIndex + 1), 5000), // Longer delays for imports
  });
};

export const useActivateSingleLeadFromImport = () => {
  const queryClient = useQueryClient();
  
  return useMutation<
    TActivateSingleImportResponseData,
    TErrorResponse,
    { importId: string; resultId: string }
  >({
    mutationKey: [leadsQueryKeys.read, "activateSingleLeadFromImport"],
    mutationFn: ({ importId, resultId }) =>
      leadsService.activateSingleLeadFromImport(importId, resultId),
    onMutate: async () => {
      // Cancel any outgoing refetches
      await Promise.all([
        queryClient.cancelQueries({ queryKey: leadsQueryKeys.lists() }),
        queryClient.cancelQueries({ queryKey: leadsQueryKeys.stats() }),
        queryClient.cancelQueries({ queryKey: leadsQueryKeys.overview() })
      ]);
      
      // Snapshot previous values
      const previousLeads = queryClient.getQueryData(leadsQueryKeys.lists());
      const previousStats = queryClient.getQueryData(leadsQueryKeys.stats());
      const previousOverview = queryClient.getQueryData(leadsQueryKeys.overview());
      
      return { previousLeads, previousStats, previousOverview };
    },
    onSuccess: async () => {
      // Use synchronous cache invalidation to prevent race conditions
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.overview() })
        ]);
      } catch (error) {

      }
      
      successToast("Lead activated successfully");
    },
    onError: (error, variables, context) => {

      // Restore previous values on error
      const ctx = context as { previousLeads?: any; previousStats?: any; previousOverview?: any } | undefined;
      if (ctx?.previousLeads) {
        queryClient.setQueryData(leadsQueryKeys.lists(), ctx.previousLeads);
      }
      if (ctx?.previousStats) {
        queryClient.setQueryData(leadsQueryKeys.stats(), ctx.previousStats);
      }
      if (ctx?.previousOverview) {
        queryClient.setQueryData(leadsQueryKeys.overview(), ctx.previousOverview);
      }
    },
    // Enhanced retry logic for single lead activation
    retry: (failureCount, error) => {
      if (failureCount < 3) {
        const errorMessage = error && typeof error === 'object' && 'message' in error 
          ? (error as { message: string }).message 
          : '';
          
        if (errorMessage.includes('500') || 
            errorMessage.includes('502') || 
            errorMessage.includes('503') ||
            errorMessage.includes('timeout') || 
            errorMessage.includes('Network')) {
          return true;
        }
      }
      return false;
    },
    retryDelay: (attemptIndex) => Math.min(800 * (attemptIndex + 1), 4000),
  });
};

// Preview of what deleting an import would remove (counts only). Lazy: only
// fetches when an importId is provided (the confirm dialog passes it on open).
export const useGetImportDeletionPreview = (importId?: string) => {
  return useQuery({
    queryKey: [leadsQueryKeys.read, "getImportDeletionPreview", importId],
    queryFn: () => leadsService.getImportDeletionPreview(importId as string),
    enabled: !!importId,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });
};

// Delete an entire import (the uploaded list) + the leads it created. The
// backend keeps pre-existing/updated leads and protects worked ones
// (sold/underwriting/appointments). Deleted leads land in Recently Deleted.
export const useDeleteImport = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [leadsQueryKeys.read, "deleteImport"],
    mutationFn: (importId: string) => leadsService.deleteImport(importId),
    onSuccess: async (data) => {
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.overview() }),
          queryClient.invalidateQueries({ queryKey: [leadsQueryKeys.read, "getLeadsImportList"] }),
          queryClient.invalidateQueries({ queryKey: ["leads"] }),
        ]);
      } catch {
        // data refreshes on next navigation
      }
      const r = data as { deleted?: number; skippedProtected?: number } | undefined;
      const deleted = r?.deleted ?? 0;
      const kept = r?.skippedProtected ?? 0;
      successToast(
        deleted === 0 && kept === 0
          ? "Empty list removed."
          : `Deleted ${deleted.toLocaleString()} lead${deleted === 1 ? "" : "s"} from this upload` +
              (kept > 0 ? ` · kept ${kept} worked lead${kept === 1 ? "" : "s"}` : ""),
      );
    },
  });
};

// Move all leads an import created into a target pipeline stage. Pre-existing
// (updated) leads aren't touched and worked leads (sold/underwriting/appt) keep
// their stage. Reversible — it's just a status change.
export const useMoveImportToStage = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: [leadsQueryKeys.read, "moveImportToStage"],
    mutationFn: ({ importId, newStatus }: { importId: string; newStatus: string }) =>
      leadsService.moveImportToStage(importId, newStatus),
    onSuccess: async (data) => {
      try {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.lists() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.stats() }),
          queryClient.invalidateQueries({ queryKey: leadsQueryKeys.overview() }),
          queryClient.invalidateQueries({ queryKey: ["leads"] }),
        ]);
      } catch {
        // data refreshes on next navigation
      }
      const r = data as { moved?: number; skippedProtected?: number; newStatus?: string } | undefined;
      const moved = r?.moved ?? 0;
      const kept = r?.skippedProtected ?? 0;
      successToast(
        `Moved ${moved.toLocaleString()} lead${moved === 1 ? "" : "s"} to "${r?.newStatus ?? "pipeline"}"` +
          (kept > 0 ? ` · kept ${kept} worked lead${kept === 1 ? "" : "s"}` : ""),
      );
    },
  });
};
